import { useEffect, useRef, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isValidName, isValidPhoneNumber } from "../../../utils/index";
import {
  setUserEditBoxHeight,
  setUserUpdateDetails,
  setError,
  putUserDetails,
  setRemoveUserPopper,
  deleteUser,
  checkAdmins,
} from "../../../redux/slices/sellerDataUserListSlice";
import {
  CircularProgress,
  useTheme,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
} from "@mui/material";

import {
  UpdateFadeBox,
  ArrowBox,
  Arrow,
  ArrowBorder,
  UpdateUserBox,
  UpdateUserMainBox,
  UpdateTextField,
  UpdateSelectBox,
  UpdateSelectTag,
  UpdateSelect,
  UpdateButtonBox,
  UpdateButton,
  ErrorMessageHelper,
  RemoveUserBox,
  RemoveUserContent,
  RemoveUserHeader,
  RemoveUserPara,
  FormControlForSelect,
} from "./styles/index";

export const UserListUpdate = memo((props) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { state, seller } = useSelector((state) => ({
    state: state.sellerDataUserList,
    seller: state.seller,
  }));

  const updateContainerRef = useRef(null);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    disablePortal: true,
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        // width: 150,
      },
    },
  };

  const apiData = (data) => {
    return {
      user_first_name: data.firstName,
      user_last_name: data.lastName,
      user_email: data.email,
      user_phone: data.phoneNumber,
      role_id: data.role,
      sub_role_type_id: data.subRole,
      additional_access_id: [...data.additionalAccess],
      preferred_language_type: data.language,
    };
  };

  const updateContainerHeight = () => {
    if (updateContainerRef.current) {
      const newHeight = updateContainerRef.current.clientHeight;
      dispatch(setUserEditBoxHeight(newHeight));
    }
  };

  const scrollToTop = () => {
    const rect = updateContainerRef.current.getBoundingClientRect();
    window.scrollTo({
      top: rect.y + window.pageYOffset - 100,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    updateContainerHeight();

    window.addEventListener("resize", updateContainerHeight);

    return () => {
      window.removeEventListener("resize", updateContainerHeight);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    updateContainerHeight();
    // eslint-disable-next-line
  }, [state.error, state.removeUserPopper]);

  const handleFormChange = (e) => {
    if (e.target.name === "firstName") {
      const checkValidation = isValidName(e.target.value);

      if (!checkValidation.valid)
        dispatch(
          setError({
            ...state.error,
            firstName: checkValidation.error,
          })
        );
      else
        dispatch(
          setError({
            ...state.error,
            firstName: "",
          })
        );

      dispatch(
        setUserUpdateDetails({
          ...state.userUpdateDetails,
          firstName: e.target.value,
        })
      );
    } else if (e.target.name === "lastName") {
      const checkValidation = isValidName(e.target.value);

      if (!checkValidation.valid)
        dispatch(
          setError({
            ...state.error,
            lastName: checkValidation.error,
          })
        );
      else
        dispatch(
          setError({
            ...state.error,
            lastName: "",
          })
        );

      dispatch(
        setUserUpdateDetails({
          ...state.userUpdateDetails,
          lastName: e.target.value,
        })
      );
    } else if (e.target.name === "phoneNumber") {
      const checkValidation = isValidPhoneNumber(e.target.value);

      if (!checkValidation.valid)
        dispatch(
          setError({
            ...state.error,
            phoneNumber: checkValidation.error,
          })
        );
      else
        dispatch(
          setError({
            ...state.error,
            phoneNumber: "",
          })
        );

      dispatch(
        setUserUpdateDetails({
          ...state.userUpdateDetails,
          phoneNumber: e.target.value,
        })
      );
    } else if (e.target.name === "role") {
      dispatch(
        checkAdmins({
          flag: "AC",
          seller_id: props.sellerId,
          user_email: "",
          type: "update",
          data: e.target.value,
        })
      );
    } else if (e.target.name === "additionalAccess") {
      dispatch(
        setUserUpdateDetails({
          ...state.userUpdateDetails,
          additionalAccess: [...e.target.value],
        })
      );
    } else if (e.target.name === "subRole") {
      dispatch(
        setUserUpdateDetails({
          ...state.userUpdateDetails,
          subRole: e.target.value,
        })
      );
    } else if (e.target.name === "language") {
      dispatch(
        setUserUpdateDetails({
          ...state.userUpdateDetails,
          language: e.target.value,
        })
      );
    }
  };

  const handleRole = (selectedRole) => {
    const selected = state.roles?.find((role) => role.role_id === selectedRole);
    return selected?.role_en ? selected.role_en : null;
  };

  const handleSubRole = (selectedSubRole) => {
    const selected = state.subRoles?.find(
      (subRole) => subRole.sub_role_type_id === selectedSubRole
    );
    return selected?.sub_role_type_en ? selected.sub_role_type_en : null;
  };

  const handleAdditionAccess = (selectedItems) => {
    let items = "";
    selectedItems?.map((selectedItem, i) => {
      const selected = state.userAccess?.find(
        (item) => item.additional_access_id === selectedItem
      );

      if (i !== 0) items = items + "; " + selected.additional_access_en;
      else items = items + selected.additional_access_en;

      return null;
    });
    return items.length ? items : null;
  };

  const handleLanguage = (selectedLanguage) => {
    const selected = state.languages?.find(
      (language) => language.preferred_language_type === selectedLanguage
    );
    return selected?.preferred_language_description
      ? selected.preferred_language_description
      : null;
  };

  const handleUpdateSubmit = () => {
    if (state.isformDataChange) {
      const userData = apiData(state.userUpdateDetails);
      dispatch(putUserDetails({ userData: userData, user_id: props.userId }));
    }
  };

  const handleRemoveUserPopper = () => {
    if (state.users.length > 3) scrollToTop();
    dispatch(setRemoveUserPopper(true));
  };

  const handleCloseRemovePopper = () => {
    scrollToTop();
    dispatch(setRemoveUserPopper(false));
  };

  const handleRemoveUser = () => {
    dispatch(deleteUser({ user_id: props.userId }));
  };

  return (
    <UpdateFadeBox
      in={props.open === props.index}
      theme={theme}
      styles={props.width}
      isMobile={props.isMobile}
    >
      <ArrowBox isMobile={props.isMobile} ref={updateContainerRef} {...props}>
        {!state.removeUserPopper ? (
          // !state.userDetailsLoading ? (
          <UpdateUserBox>
            <UpdateUserMainBox>
              <UpdateTextField
                id="standard-basic"
                label="First Name *"
                variant="standard"
                name="firstName"
                error={state.error.firstName?.trim() ? true : false}
                helperText={
                  <ErrorMessageHelper>
                    {state.error.firstName}
                  </ErrorMessageHelper>
                }
                onChange={handleFormChange}
                value={state.userUpdateDetails.firstName}
              ></UpdateTextField>
              <UpdateTextField
                id="standard-basic"
                label="Last Name *"
                variant="standard"
                name="lastName"
                error={state.error.lastName?.trim() ? true : false}
                helperText={
                  <ErrorMessageHelper>
                    {state.error.lastName}
                  </ErrorMessageHelper>
                }
                onChange={handleFormChange}
                value={state.userUpdateDetails.lastName}
              ></UpdateTextField>
              <UpdateTextField
                id="standard-basic"
                label="Email *"
                variant="standard"
                name="email"
                value={state.userUpdateDetails.email}
                disabled
                sx={{
                  input: {
                    fontSize: "0.8rem",
                  },
                }}
              ></UpdateTextField>
              <UpdateTextField
                id="standard-basic"
                label="Phone Number"
                variant="standard"
                name="phoneNumber"
                onChange={handleFormChange}
                error={state.error.phoneNumber?.trim() ? true : false}
                helperText={
                  <ErrorMessageHelper>
                    {state.error.phoneNumber}
                  </ErrorMessageHelper>
                }
                value={state.userUpdateDetails.phoneNumber}
              ></UpdateTextField>

              <UpdateSelectBox>
                <FormControlForSelect>
                  <UpdateSelectTag
                    sx={{
                      color: state.error.role.length !== 0 ? "red" : "#4A4F55",
                    }}
                  >
                    Role *
                  </UpdateSelectTag>
                  <UpdateSelect
                    variant="standard"
                    name="role"
                    value={state.userUpdateDetails.role}
                    onChange={handleFormChange}
                    // onBlur={handleAddAdmin}
                    renderValue={handleRole}
                    MenuProps={MenuProps}
                  >
                    {state.roles?.map((role) => (
                      <MenuItem key={role.role_id} value={role.role_id}>
                        <Checkbox
                          checked={
                            state.userUpdateDetails.role === role.role_id
                              ? true
                              : false
                          }
                        />
                        <ListItemText primary={role.role_en} />
                      </MenuItem>
                    ))}
                  </UpdateSelect>

                  <FormHelperText
                    sx={{
                      position: "absolute",
                      left: -14,
                      bottom: props.isMobile ? -20 : -30,
                      fontSize: props.isMobile ? "0.5rem" : "0.55rem",
                      color: "red",
                    }}
                  >
                    {state.error.role}
                  </FormHelperText>
                </FormControlForSelect>
              </UpdateSelectBox>

              <UpdateSelectBox>
                <FormControlForSelect>
                  <UpdateSelectTag
                    sx={{
                      color:
                        state.error.subRole.length !== 0 ? "red" : "#4A4F55",
                    }}
                  >
                    Type *
                  </UpdateSelectTag>
                  <UpdateSelect
                    variant="standard"
                    name="subRole"
                    value={state.userUpdateDetails.subRole}
                    onChange={handleFormChange}
                    // onBlur={handleAddAdmin}
                    renderValue={handleSubRole}
                    MenuProps={MenuProps}
                  >
                    {state.subRoles?.map((subRole) => (
                      <MenuItem
                        key={subRole.sub_role_type_id}
                        value={subRole.sub_role_type_id}
                      >
                        <Checkbox
                          checked={
                            state.userUpdateDetails.subRole ===
                            subRole.sub_role_type_id
                              ? true
                              : false
                          }
                        />
                        <ListItemText primary={subRole.sub_role_type_en} />
                      </MenuItem>
                    ))}
                  </UpdateSelect>

                  <FormHelperText
                    sx={{
                      position: "absolute",
                      left: -14,
                      bottom: props.isMobile ? -20 : -30,
                      fontSize: props.isMobile ? "0.5rem" : "0.55rem",
                      color: "red",
                    }}
                  >
                    {state.error.subRole}
                  </FormHelperText>
                </FormControlForSelect>
              </UpdateSelectBox>

              <UpdateSelectBox sx={{ marginTop: "10px" }}>
                <UpdateSelectTag>Additional Access</UpdateSelectTag>
                <UpdateSelect
                  multiple
                  variant="standard"
                  name="additionalAccess"
                  value={state.userUpdateDetails.additionalAccess}
                  onChange={handleFormChange}
                  renderValue={handleAdditionAccess}
                  MenuProps={MenuProps}
                >
                  {state.userAccess?.map((item) => (
                    <MenuItem
                      key={item.additional_access_id}
                      value={item.additional_access_id}
                    >
                      <Checkbox
                        checked={
                          state.userUpdateDetails?.additionalAccess?.indexOf(
                            item.additional_access_id
                          ) > -1
                        }
                      />
                      <ListItemText primary={item.additional_access_en} />
                    </MenuItem>
                  ))}
                </UpdateSelect>
              </UpdateSelectBox>

              <UpdateSelectBox>
                <UpdateSelectTag>Preferred Language for Emails</UpdateSelectTag>
                <UpdateSelect
                  variant="standard"
                  name="language"
                  value={state.userUpdateDetails.language}
                  onChange={handleFormChange}
                  renderValue={handleLanguage}
                  MenuProps={MenuProps}
                >
                  {state.languages?.map((language) => (
                    <MenuItem
                      key={language.preferred_language_type}
                      value={language.preferred_language_type}
                    >
                      <Checkbox
                        checked={
                          state.userUpdateDetails?.language?.indexOf(
                            language.preferred_language_type
                          ) > -1
                        }
                      />
                      <ListItemText
                        primary={language.preferred_language_description}
                      />
                    </MenuItem>
                  ))}
                </UpdateSelect>
              </UpdateSelectBox>
            </UpdateUserMainBox>
            <UpdateButtonBox>
              <UpdateButton
                variant="contained"
                disabled={!state.isformDataChange}
                onClick={handleUpdateSubmit}
                sx={{ color: "white" }}
              >
                {state.onSubmitLoading && state.isformDataChange ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: "white" }}
                  ></CircularProgress>
                ) : (
                  "Save Updates"
                )}
              </UpdateButton>
              <UpdateButton
                onClick={handleRemoveUserPopper}
                sx={{ ":hover": { backgroundColor: "transparent" } }}
                disabled={
                  state.userDetails.user_email?.toLowerCase() ===
                  seller.msal.user_email?.toLowerCase()
                }
              >
                {state.userDetails.user_email?.toLowerCase() ===
                seller.msal.user_email?.toLowerCase()
                  ? ""
                  : "Remove User"}
              </UpdateButton>
            </UpdateButtonBox>
          </UpdateUserBox>
        ) : (
          // ) : (
          //   <LoadingBox>
          //     <CircularProgress
          //       size={10}
          //       sx={{ color: "grey" }}
          //     ></CircularProgress>
          //   </LoadingBox>
          // )
          <RemoveUserBox>
            <RemoveUserContent>
              <RemoveUserHeader>
                Are you sure you want to remove this user?
              </RemoveUserHeader>
              <RemoveUserPara>
                Access will be revoked and they will be removed from any
                notification lists.
              </RemoveUserPara>
            </RemoveUserContent>

            <UpdateButtonBox>
              <UpdateButton
                variant="contained"
                sx={{
                  backgroundColor: "white",
                  color: "#005078",
                  ":hover": { backgroundColor: "white", color: "#005078" },
                }}
                onClick={handleRemoveUser}
              >
                {state.onRemoveLoading ? (
                  <CircularProgress size={20}></CircularProgress>
                ) : (
                  "Remove User"
                )}
              </UpdateButton>
              <UpdateButton
                sx={{ ":hover": { backgroundColor: "transparent" } }}
                onClick={handleCloseRemovePopper}
              >
                Cancel
              </UpdateButton>
            </UpdateButtonBox>
          </RemoveUserBox>
        )}
        <Arrow isMobile={props.isMobile} height={state.userEditBoxHeight} />
        <ArrowBorder
          isMobile={props.isMobile}
          height={state.userEditBoxHeight}
        />
      </ArrowBox>
    </UpdateFadeBox>
  );
});
